(ns arthur.domain.correction
  "Pure commands that author and resolve correction layers.

  Evaluation belongs to `channel`; this namespace only constructs a layer,
  places it on its owning node, and refuses a document that would not be valid."
  (:require [arthur.domain.channel :as ch]
            [arthur.domain.clip :as clip]
            [arthur.domain.node :as node]))

(def ^:private supported-paths #{[:xform :rot] [:xform :pos]})
(def ^:private motions #{:constant :ramp :return})

(defn- finite? [x] (and (number? x) (js/Number.isFinite x)))

(defn- numeric-value? [v]
  (or (finite? v)
      (and (vector? v) (pos? (count v)) (every? finite? v))))

(defn- same-shape? [a b]
  (or (and (number? a) (number? b))
      (and (vector? a) (vector? b) (= (count a) (count b)))))

(defn- expected-value? [path v]
  (case path
    [:xform :rot] (finite? v)
    [:xform :pos] (and (vector? v) (= 2 (count v)) (every? finite? v))
    false))

(defn- values-channel
  [{:keys [motion support delta start end peak peak-frame]}]
  (let [[a b] support]
    (case motion
      :constant (ch/framed delta)
      :ramp (ch/keyed {a start, (dec b) end} :linear)
      :return (ch/keyed {a start, peak-frame peak, (dec b) start} :linear)
      nil)))

(defn- invalid
  [path {:keys [id support motion delta start end peak peak-frame]} existing]
  (let [[a b] (when (and (vector? support) (= 2 (count support))) support)
        samples (case motion :constant [delta] :ramp [start end]
                      :return [start peak] [])]
    (cond
      (nil? id) "a correction needs an ID"
      (some #(= id (:id %)) existing) "the correction ID is already used on this channel"
      (not (contains? supported-paths path)) "that property does not support correction authoring"
      (not (contains? motions motion)) "choose constant, ramp, or return motion"
      (not (and (integer? a) (integer? b) (< a b)))
      "support must be an increasing [in out) of whole owner frames"
      (not-every? numeric-value? samples) "correction values must be finite numbers"
      (not-every? #(expected-value? path %) samples)
      "correction values do not have the property's shape"
      (and (= :ramp motion) (< (- b a) 2)) "a ramp needs at least two samples"
      (and (= :return motion) (< (- b a) 3)) "return motion needs at least three samples"
      (and (= :return motion)
           (not (and (integer? peak-frame) (< a peak-frame (dec b)))))
      "the return peak must be a whole owner frame inside both endpoints"
      (and (#{:ramp :return} motion) (not (same-shape? start (if (= :ramp motion) end peak))))
      "motion endpoints must have the same shape")))

(defn- finish [candidate selection]
  (if-let [why (first (clip/problems candidate))]
    {:refused why}
    {:clip candidate :selection selection}))

(defn add
  "Append one offset correction to a node channel.

  Support and value keys are in the selected node's own frames. Defaults are
  materialized through `node/channels`, so correcting an unkeyed transform does
  not need a special representation."
  [document sid node-id path spec]
  (let [n (get-in document [:symbols sid :nodes node-id])
        base (when n (get (node/channels n) path))
        existing (:over base)
        why (cond
              (nil? (clip/symbol document sid)) "the owning symbol does not exist"
              (nil? n) "the correction target does not exist"
              (nil? base) "the correction target has no such channel"
              :else (invalid path spec existing))]
    (if why
      {:refused why}
      (let [layer (ch/layer (:id spec) (:support spec) :offset (values-channel spec))
            corrected (update base :over (fnil conj []) layer)
            candidate (assoc-in document [:symbols sid :nodes node-id :channels path] corrected)]
        (finish candidate node-id)))))

(defn remove-layer
  "Remove one named layer, refusing when a later layer depended on its shape."
  [document sid node-id path layer-id]
  (let [at [:symbols sid :nodes node-id :channels path]
        c (get-in document at)
        layers (:over c)]
    (cond
      (nil? c) {:refused "the correction channel does not exist"}
      (not-any? #(= layer-id (:id %)) layers) {:refused "the correction does not exist"}
      :else (finish (assoc-in document at
                              (assoc c :over (vec (remove #(= layer-id (:id %)) layers))))
                    node-id))))

(defn retry-layer
  "Clear one recorded conflict when the complete resulting stack is valid."
  [document sid node-id path layer-id]
  (let [at [:symbols sid :nodes node-id :channels path]
        c (get-in document at)
        found (some #(when (= layer-id (:id %)) %) (:over c))]
    (cond
      (nil? found) {:refused "the correction does not exist"}
      (nil? (:conflict found)) {:refused "the correction has no recorded conflict"}
      :else
      (finish (update-in document (conj at :over)
                         (fn [layers]
                           (mapv #(if (= layer-id (:id %)) (dissoc % :conflict) %) layers)))
              node-id))))

(defn borrow-pose [document sid {:keys [from through donor head?] :as spec} store]
  (let [frames (get-in document [:symbols sid :frames])
        ids (into #{} (mapcat :nodes)
                  (filter #(= sid (:symbol %)) (vals (:features document))))
        ids (cond-> ids head? (conj :head))
        paths (for [id ids [path c] (get-in document [:symbols sid :nodes id :channels])]
                [id path c])]
    (cond
      (not (and (integer? frames) (every? integer? [from through donor])
                (<= 0 from through (dec frames)) (<= 0 donor (dec frames))))
      {:refused "choose whole face frames within this symbol"}
      (<= from donor through) {:refused "choose a clean donor outside the repair interval"}
      (empty? paths) {:refused "this symbol has no tracked face features"}
      (not-any? (fn [[_ path c]]
                  (and (= path [:geom :pts])
                       (not (ch/nothing? (ch/value-at (dissoc c :repairs :over) donor store)))))
                paths)
      {:refused "the donor has no face pose; choose another frame"}
      :else
      (finish (reduce (fn [doc [node path _]]
                        (update-in doc [:symbols sid :nodes node :channels path :repairs]
                                   (fnil conj []) (select-keys spec [:id :from :through :donor])))
                      document paths) nil))))

(declare remove-eye-keys)

(defn remove-repair [document sid repair-id]
  {:clip (reduce (fn [doc [id path]]
                   (update-in doc [:symbols sid :nodes id :channels path :repairs]
                              #(vec (remove (fn [r] (= repair-id (:id r))) %))))
                 (-> document
                     (remove-eye-keys sid repair-id :l) :clip
                     (remove-eye-keys sid repair-id :r) :clip)
                 (for [[id n] (get-in document [:symbols sid :nodes])
                       [path c] (:channels n) :when (:repairs c)] [id path]))})

(defn eye-key
  "Key a procedural lid adjustment and gaze offset over one repair interval."
  [document sid repair-id side frame {:keys [opening gaze-x gaze-y]}]
  (let [outer (keyword (str "eye-" (name side)))
        inner (keyword (str "eye-" (name side) "-in"))
        iris (keyword (str "iris-" (name side)))
        repair (some #(when (= repair-id (:id %)) %)
                     (get-in document [:symbols sid :nodes outer :channels [:geom :pts] :repairs]))
        {:keys [from through]} repair
        layer-id (str repair-id "/eye/" (name side))
        edits [[outer [:geom :pts] :eye-opening opening 1]
               [inner [:geom :pts] :eye-opening opening 1]
               [iris [:xform :pos] :offset [gaze-x gaze-y] [0 0]]]]
    (cond
      (nil? repair) {:refused "this eye has no such repair interval"}
      (not (and (integer? frame) (<= from frame through)))
      {:refused "move the playhead inside this repair interval"}
      (not (and (every? finite? [opening gaze-x gaze-y]) (<= 0 opening 3)))
      {:refused "eye opening must be between 0 and 3; gaze offsets must be finite"}
      :else
      (finish
       (reduce
        (fn [doc [id path op value neutral]]
          (update-in doc [:symbols sid :nodes id :channels path :over]
                     (fn [layers]
                       (let [existing (some #(when (= layer-id (:id %)) %) layers)
                             values (or (:values existing)
                                        (ch/keyed {from neutral through neutral} :linear))
                             layer (ch/layer layer-id [from (inc through)] op
                                             (assoc-in values [:keys frame] value))]
                         (conj (vec (remove #(= layer-id (:id %)) layers)) layer)))))
        document edits)
       nil))))

(defn remove-eye-keys [document sid repair-id side]
  (let [layer-id (str repair-id "/eye/" (name side))]
    {:clip (reduce (fn [doc [id path]]
                     (update-in doc [:symbols sid :nodes id :channels path :over]
                                #(vec (remove (fn [l] (= layer-id (:id l))) %))))
                   document
                   (for [[id n] (get-in document [:symbols sid :nodes])
                         [path c] (:channels n) :when (:over c)] [id path]))}))
